import React, { useState, useEffect } from 'react';
import Header from '../components/header';
import Footer from '../components/footer';
import FloatingChatButton from '../components/FloatingChatButton';
import ContactForm from '../components/ContactForm';
import TestimonialVideoCarousel from '../components/TestimonialVideoCarousel';
import CaseStudiesCarousel from '../components/CaseStudiesCarousel';
import TestimonialsCarousel from '../components/TestimonialsCarousel';
import { Link, useSearchParams } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://medical-linkers.onrender.com';

const BlogsMain = () => {
  const [searchParams] = useSearchParams();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(searchParams.get('country') || '');
  const [categories, setCategories] = useState([]);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  useEffect(() => {
    fetchBlogs();
    fetchCategories();
  }, [currentPage, selectedCategory, selectedCountry]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 12,
        status: 'published'
      });

      if (selectedCategory) {
        params.append('category', selectedCategory);
      }

      if (selectedCountry) {
        params.append('country', selectedCountry);
      }

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await fetch(`${API_BASE_URL}/api/blogs?${params}`);
      const data = await response.json();
      
      setBlogs(data.blogs || []);
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/blogs/meta/categories`);
      const data = await response.json();
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchBlogs();
  };

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const handleCountryFilter = (country) => {
    setSelectedCountry(country);
    setCurrentPage(1);
  };

  // Mock video data
  const videos = [
    { id: 1, title: "Understanding Medical Tourism Benefits", thumbnail: "/images/video-thumb-1.jpg" },
    { id: 2, title: "Best Hospitals for International Patients", thumbnail: "/images/video-thumb-2.jpg" },
    { id: 3, title: "Recovery Tips After Surgery", thumbnail: "/images/video-thumb-3.jpg" },
    { id: 4, title: "Choosing the Right Treatment", thumbnail: "/images/video-thumb-4.jpg" }
  ];

  const handleVideoNext = () => {
    setCurrentVideoIndex((prev) => (prev + 1) % videos.length);
  };

  const handleVideoPrev = () => {
    setCurrentVideoIndex((prev) => (prev - 1 + videos.length) % videos.length);
  };

  const visibleVideos = videos.slice(currentVideoIndex, currentVideoIndex + 3);
  if (visibleVideos.length < 3) {
    visibleVideos.push(...videos.slice(0, 3 - visibleVideos.length));
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    }).replace(/\//g, '-');
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-grow">
        {/* Blogs Section */}
        <section className="py-16 px-6">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h3 className="text-4xl font-bold">
                <span className="text-blue-600">Blogs</span>
              </h3>
            </div>

            {/* Search and Filter */}
            <div className="mb-8">
              <div className="flex flex-col gap-4 justify-center items-center">
                {/* Search */}
                <form onSubmit={handleSearch} className="flex gap-2">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search blogs..."
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="submit"
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Search
                  </button>
                </form>

                {/* Country Filter */}
                <div className="flex gap-2 flex-wrap justify-center">
                  <button
                    onClick={() => handleCountryFilter('')}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      selectedCountry === '' ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    All Countries
                  </button>
                  <button
                    onClick={() => handleCountryFilter('india')}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      selectedCountry === 'india' ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    🇮🇳 India
                  </button>
                  <button
                    onClick={() => handleCountryFilter('thailand')}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      selectedCountry === 'thailand' ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    🇹🇭 Thailand
                  </button>
                  <button
                    onClick={() => handleCountryFilter('south-korea')}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      selectedCountry === 'south-korea' ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    🇰🇷 South Korea
                  </button>
                  <button
                    onClick={() => handleCountryFilter('turkey')}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      selectedCountry === 'turkey' ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    🇹🇷 Turkey
                  </button>
                </div>

                {/* Category Filter */}
                <div className="flex gap-2 flex-wrap justify-center">
                  <button
                    onClick={() => handleCategoryFilter('')}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      selectedCategory === '' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    All Categories
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => handleCategoryFilter(category)}
                      className={`px-4 py-2 rounded-lg transition-colors ${
                        selectedCategory === category ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center items-center min-h-96">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <>
                <div className="grid lg:grid-cols-4 gap-8">
                  {/* Main Content - Blogs Grid */}
                  <div className="lg:col-span-3">
                    <div className="grid md:grid-cols-2 gap-8">
                      {blogs.map((blog) => (
                        <div key={blog._id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                          <div className="aspect-video overflow-hidden">
                            <img 
                              src={blog.featuredImage} 
                              alt={blog.title}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                e.target.src = "https://dummyimage.com/600x400/eeeeee";
                              }}
                            />
                          </div>
                          
                          <div className="p-6">
                            <div className="mb-3">
                              <p className="text-sm text-gray-600">
                                Published on: &nbsp; {formatDate(blog.publishedAt || blog.createdAt)}
                              </p>
                            </div>
                            
                            <div className="mb-4">
                              <h4 className="text-xl font-semibold text-gray-800 leading-tight hover:text-blue-600 transition-colors">
                                <Link to={`/blogs/${blog.slug}`}>{blog.title}</Link>
                              </h4>
                            </div>

                            <p className="text-gray-600 mb-4 line-clamp-3">
                              {blog.excerpt}
                            </p>

                            <div className="flex items-center justify-between">
                              <Link 
                                to={`/blogs/${blog.slug}`}
                                className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center transition-colors"
                              >
                                Read More <span className="ml-1">→</span>
                              </Link>
                              <span className="text-sm text-gray-500">
                                {blog.readTime} min read
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="mt-8 flex justify-center">
                        <nav className="flex space-x-2">
                          <button
                            onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                            disabled={currentPage === 1}
                            className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                          >
                            Previous
                          </button>
                          
                          {[...Array(totalPages)].map((_, i) => (
                            <button
                              key={i + 1}
                              onClick={() => setCurrentPage(i + 1)}
                              className={`px-4 py-2 text-sm font-medium rounded-md ${
                                currentPage === i + 1
                                  ? 'bg-blue-600 text-white'
                                  : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {i + 1}
                            </button>
                          ))}
                          
                          <button
                            onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                          >
                            Next
                          </button>
                        </nav>
                      </div>
                    )}
                  </div>

                  {/* Sidebar */}
                  <div className="lg:col-span-1">
                    <div className="bg-white rounded-lg shadow-lg p-6">
                      <div className="mb-6">
                        <h4 className="text-xl font-semibold text-gray-800 mb-4">Categories</h4>
                        <div className="space-y-2">
                          <ul className="space-y-2">
                            {categories.map((category) => (
                              <li key={category}>
                                <button
                                  onClick={() => handleCategoryFilter(category)}
                                  className="text-gray-600 hover:text-blue-600 transition-colors block py-1 w-full text-left"
                                >
                                  {category}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto max-w-7xl px-6">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="grid md:grid-cols-2">
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Get Expert Medical Consultation</h3>
                  <ContactForm formType="blog-inquiry" />
                </div>
                <div className="hidden md:block">
                  <div className="h-full">
                    <img 
                      src="/images/image copy 22.png" 
                      alt="Medical consultation"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = "https://dummyimage.com/600x400/eeeeee/999999?text=Medical+Consultation";
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Recent Case Studies Section */}
        <CaseStudiesCarousel 
          title="Recent Case Studies"
        />

        {/* Testimonials Section */}
        <TestimonialsCarousel 
          title="Real Stories, Real Impact"
          limit={6}
        />

        {/* Call to Action */}
        <section className="py-12 bg-blue-600">
          <div className="container mx-auto max-w-7xl px-6">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="mb-4 md:mb-0">
                <h4 className="text-2xl font-semibold text-white">Search For Your Treatment</h4>
              </div>
              <div>
                <a href="#" className="bg-white text-blue-600 px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors inline-block font-medium">
                  Explore More
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonial Videos Section */}
        <TestimonialVideoCarousel 
          title="Patient Success Stories & Videos"
          subtitle="Watch inspiring stories from patients who received successful treatment"
          limit={6}
        />
      </main>

      <Footer />
      <FloatingChatButton />
    </div>
  );
};

export default BlogsMain;
